import React, { useEffect, useMemo, useState } from "react";
import {
	FormGroup,
	Label,
	Input,
	FormText,
	Row,
	Col,
	Spinner,
	Alert,
	Badge
} from "reactstrap";
import Select, { components } from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationState } from "store";
import { getAppQuotaRequest, installationsGetRequest, mappingGetByInstallRequest } from "store/integrations/action";
import { templateListGetRequest } from "store/templates/action";
import type { Template } from "store/templates/types";
import CreatableSelect from "react-select/creatable";
import validator from "validator"; // npm install validator

const emptyChannels: Record<ChannelType, boolean> = {
	EMAIL: false,
	WHATSAPP: false
};

// Account (Install) Option Renderer
const AccountOption = (props: any) => {
	const { data } = props;
	return (
		<components.Option {...props}>
			<div className="d-flex align-items-center gap-2">
				{data.logo && (
					<img src={data.logo} alt={data.label} width={24} height={24} style={{ borderRadius: 4 }} />
				)}
				<div>
					<div className="fw-semibold">{data.label}</div>
					<div className="small">
						{data.accountInfo}
						{data.status && (
							<Badge color={data.status === "ACTIVE" ? "success" : "secondary"} pill className="ms-2">
								{data.status}
							</Badge>
						)}
					</div>
				</div>
			</div>
		</components.Option>
	);
};
const AccountSingleValue = (props: any) => {
	const { data } = props;
	return (
		<components.SingleValue {...props}>
			<div className="d-flex align-items-center gap-2">
				{data.logo && (
					<img src={data.logo} alt={data.label} width={20} height={20} style={{ borderRadius: 4 }} />
				)}
				<span className="fw-semibold">{data.label}</span>
			</div>
		</components.SingleValue>
	);
};

// Template Option Renderer
const TemplateOption = (props: any) => {
	const { data } = props;
	return (
		<components.Option {...props}>
			<div>
				<div className="fw-semibold">{data.label}</div>
				<div className="small">{data.id}</div>
			</div>
		</components.Option>
	);
};
const TemplateSingleValue = (props: any) => {
	const { data } = props;
	return (
		<components.SingleValue {...props}>
			<span className="fw-semibold">{data.label}</span>
			<span className="text-muted small ms-2">{data.id}</span>
		</components.SingleValue>
	);
};

// Channel meta
export const CHANNELS = [
	{ key: "EMAIL", label: "Email", icon: "ri-mail-line", helper: "Send via your verified sending domain." },
	{ key: "WHATSAPP", label: "WhatsApp", icon: "ri-whatsapp-line", helper: "Send WhatsApp via your connected account." }
] as const;

export type ChannelType = typeof CHANNELS[number]["key"];
export const CHANNEL_LABELS: Record<ChannelType, string> = Object.fromEntries(CHANNELS.map(ch => [ch.key, ch.label])) as any;
export const CHANNEL_ICONS: Record<ChannelType, string> = Object.fromEntries(CHANNELS.map(ch => [ch.key, ch.icon])) as any;
export type ChannelState = {
	channels: Record<ChannelType, boolean>;
	selectedInstallByChannel: Partial<Record<ChannelType, string>>;
	templateByChannel: Partial<Record<ChannelType, string>>;
	fromByChannel: Partial<Record<ChannelType, string>>;
};

interface StepChannelsProps {
	data: ChannelState;
	onChange: (patch: Partial<ChannelState>) => void;
}

const StepChannels: React.FC<StepChannelsProps> = ({ data, onChange }) => {
	const dispatch = useDispatch<any>();
	const tenantId = useSelector((s: ApplicationState) => s.auth.userProfile.tenant.id);
	const installations = useSelector((s: ApplicationState) => s.integration.installations);
	const loadingInstalls = useSelector((s: ApplicationState) => s.integration.loadingInstalls);
	const errorInstalls = useSelector((s: ApplicationState) => s.integration.errorInstalls);
	const templates = useSelector((s: ApplicationState) => s.templates.list);
	const loadingTemplates = useSelector((s: ApplicationState) => s.templates.dataLoading);
	const mappingRecords = useSelector((s: ApplicationState) => s.integration.mappingRecords);


	const [focusedChannel, setFocusedChannel] = useState<ChannelType>("EMAIL");
	const [fromByChannel, setFromByChannel] = useState<Partial<Record<ChannelType, string>>>({});
	const [fromError, setFromError] = useState<string | null>(null);

	useEffect(() => {
		if (tenantId) dispatch(installationsGetRequest(tenantId));
	}, [tenantId, dispatch]);

	useEffect(() => {
		const instId = data.selectedInstallByChannel[focusedChannel];
		if (instId) {
			dispatch(templateListGetRequest({ installationId: instId }, 0, 20));
		}
	}, [data.selectedInstallByChannel["EMAIL"], focusedChannel, dispatch]);

	useEffect(() => {
		if(data?.fromByChannel?.EMAIL){
			setFromByChannel({...fromByChannel, ["EMAIL"]: data?.fromByChannel?.["EMAIL"]})
		}
		else {
			setFromByChannel({...fromByChannel, ["EMAIL"]: undefined})
		}
	}, [data?.fromByChannel?.["EMAIL"], data?.channels?.["EMAIL"]]);

	// Fetch WhatsApp mapping records when install changes
	useEffect(() => {
		const instId = data.selectedInstallByChannel["WHATSAPP"];
		if (focusedChannel === "WHATSAPP" && instId) {
			dispatch(mappingGetByInstallRequest(tenantId, instId));
		}
	}, [data.selectedInstallByChannel["WHATSAPP"], focusedChannel, tenantId, dispatch]);


	// Whenever the account changes, update the from options
	const getFromForInstall = (install: any) => {
		if (!install) return [];
		try {
			const conf = JSON.parse(install.config_json || "{}");
			if (Array.isArray(conf.verified_emails)) {
				return conf.verified_emails;
			}
			if (conf.domain) {
				// Fallback: build some suggestions
				return [
					`noreply@${conf.domain}`,
					`info@${conf.domain}`,
					`alerts@${conf.domain}`
				];
			}
		} catch (e) { }
		return [];
	};

	const getAllowedDomainsForInstall = (install: any) => {
		if (!install) return [];
		try {
			const conf = JSON.parse(install.config_json || "{}");
			if (conf.domain) return [conf.domain];
			if (Array.isArray(conf.verified_domains)) return conf.verified_domains;
		} catch { }
		return [];
	};

	const validateFrom = (email: string, install: any): string | null => {
		if (!validator.isEmail(email)) return "Enter a valid email address.";
		const allowedDomains = getAllowedDomainsForInstall(install);
		if (!allowedDomains.length) return null; // Don't block if no domain is set
		const emailDomain = email.split("@")[1]?.toLowerCase();
		if (!allowedDomains.some((d: string) => emailDomain === d.toLowerCase())) {
			return `Email must match your verified domain (${allowedDomains.join(", ")}).`;
		}
		return null;
	};

	// Only show installs that support the current channel
	const channelInstalls = (channelKey: ChannelType) =>
		installations
			.filter(
				(i: any) =>
					Array.isArray(i.version?.metadata?.channels) &&
					i.version.metadata.channels.includes(channelKey)
			)
			.map((i: any) => {
				return {
					value: i.id,
					label: i.name || i.id,
					logo: i.version?.metadata?.logo || "",
					status: i.status,
					accountInfo: i.version?.metadata?.name || "",
					meta: i
				};
			});

	const templateOptions = useMemo(() => {
		// WhatsApp channel: Use mapping records as "templates"
		if (
			focusedChannel === "WHATSAPP" &&
			data.channels["WHATSAPP"]
		) {
			// Accept mapping records with no installation_id or matching install
			return mappingRecords
				.filter(rec =>
					!rec.installation_id ||
					rec.installation_id === data.selectedInstallByChannel["WHATSAPP"]
				)
				.map(rec => ({
					value: rec.id,
					label: rec.name,
					id: rec.id,
				}));
		}
		// Email/SMS/InApp: use global templates list
		return templates?.map((tpl: Template) => {
			let label = tpl.id;
			try {
				label = tpl.valuesJson?.name || tpl.id;
			} catch { }
			return {
				value: tpl.id,
				label,
				id: tpl.id,
			};
		}) || [];
	}, [
		focusedChannel,
		data.channels,
		data.selectedInstallByChannel,
		mappingRecords,
		templates,
	]);

	const toggleChannel = (ch: ChannelType, checked: boolean) => {
		if (!checked) {
			// Deselect all
			onChange({
				channels: { ...emptyChannels },
				selectedInstallByChannel: {},
				templateByChannel: {},
				fromByChannel: {},
			});
			return;
		}
		// Only enable one channel
		onChange({
			channels: { ...emptyChannels, [ch]: true },
			selectedInstallByChannel: { [ch]: data.selectedInstallByChannel[ch] },
			templateByChannel: { [ch]: data.templateByChannel[ch] },
			fromByChannel: { [ch]: fromByChannel[ch] },
		});
	};

	// When user selects an account, clear from
	const selectInstall = (ch: ChannelType, instId: string | null) => {
		setFocusedChannel(ch);
		// Call quota request if integration is selected and channel enabled
		if (instId) {
			const selectedInstall = installations.find((i: any) => i.id === instId);
			if (selectedInstall?.version?.app_id && tenantId) {
				dispatch(getAppQuotaRequest(tenantId, selectedInstall?.version?.app_id));
			}
		}
		onChange({
			selectedInstallByChannel: { ...data.selectedInstallByChannel, [ch]: instId || undefined },
			templateByChannel: { ...data.templateByChannel, [ch]: undefined }
		});
		setFromByChannel(prev => ({ ...prev, [ch]: undefined }));
	};

	// Save from selection to state, and propagate up if needed
	const selectFrom = (ch: ChannelType, email: string) => {
		setFromByChannel(prev => ({ ...prev, [ch]: email }));
		onChange({ fromByChannel: { ...fromByChannel, [ch]: email } });
	};
	const selectTemplate = (ch: ChannelType, tplId: string | null) => {
		onChange({ templateByChannel: { ...data.templateByChannel, [ch]: tplId || undefined } });
	};
	return (
		<>
			<div className="mb-4">
				<h4 className="fw-bold d-flex align-items-center mb-2" style={{ fontSize: "1.25rem" }}>
					<i className="ri-mail-send-line me-2 text-primary" style={{ fontSize: 24 }} />
					Select Campaign Channel
				</h4>
				<div className="text-muted mb-2" style={{ maxWidth: 580 }}>
					<i className="ri-information-line me-1" />
					<b>Only one channel</b> can be selected for this campaign.
					<br />
					Choose how you want to send your campaign. Each channel supports different types of integrations, templates, and sender settings.
				</div>
			</div>
			<form>
				{CHANNELS.map((ch, idx) => (
					<React.Fragment key={ch.key}>
						{/* Channel section header */}
						<Row className="align-items-center mb-2">
							<Col xs="auto" className="pe-0">
							</Col>
							<Col>
								<div className="d-flex gap-2 align-items-center">
									<Label for={`enable-${ch.key}`} className="fw-semibold fs-5 mb-0 d-flex align-items-center">
										<i className={`${ch.icon} me-2`} />
										{ch.label}
									</Label>
									<div className="form-check form-switch">
										<Input
											className="form-check-input"
											type="checkbox"
											role="switch"
											id={`enable-${ch.key}`}
											checked={!!data.channels[ch.key]}
											onChange={e => toggleChannel(ch.key, e.target.checked)}
											disabled={
												// Only enable unchecking, or checking if nothing else is checked
												!data.channels[ch.key] && Object.values(data.channels).some(val => val)
											}
										/>
									</div>
								</div>
								<div>
									<FormText color="muted" className="ms-4">{ch.helper}</FormText>
								</div>
							</Col>
						</Row>
						{/* Accounts + Templates */}
						{data.channels[ch.key] && (
							<Row className="mb-3 ms-4">
								<Col md={5}>
									<FormGroup>
										<Label className="fw-semibold mb-1">{ch.label} Account <span className="fw-bold text-danger">{" "}*</span></Label>
										{loadingInstalls ? (
											<Spinner size="sm" />
										) : errorInstalls ? (
											<Alert color="danger">{errorInstalls}</Alert>
										) : (
											<Select
												inputId={`install-select-${ch.key}`}
												options={channelInstalls(ch.key)}
												value={channelInstalls(ch.key).find(o => o.value === data.selectedInstallByChannel[ch.key]) || null}
												onChange={(opt: { value: any; }) => selectInstall(ch.key, opt?.value ?? null)}
												placeholder={`Select an account...`}
												isClearable
												onFocus={() => setFocusedChannel(ch.key)}
												noOptionsMessage={() => "No accounts connected for this channel"}
												components={{ Option: AccountOption, SingleValue: AccountSingleValue }}
											/>
										)}
										<FormText color="muted">
											Choose an integration account for {ch.label.toLowerCase()} delivery.
										</FormText>
									</FormGroup>
								</Col>
								<Col md={5}>
									{data.selectedInstallByChannel[ch.key] && (
										<FormGroup>
											<Label className="fw-semibold mb-1">{ch.label} Template<span className="fw-bold text-danger">{" "}*</span></Label>
											{loadingTemplates ? (
												<Spinner size="sm" />
											) : (
												<Select
													inputId={`template-select-${ch.key}`}
													options={templateOptions}
													value={templateOptions.find(o => o.value === data.templateByChannel[ch.key]) || null}
													onChange={(opt: { value: any; }) => selectTemplate(ch.key, opt?.value ?? null)}
													placeholder="Select a template..."
													isClearable
													noOptionsMessage={() => "No templates found for this account"}
													components={{ Option: TemplateOption, SingleValue: TemplateSingleValue }}
												/>
											)}
											<FormText color="muted">
												Pick an approved template for this channel.
											</FormText>
										</FormGroup>
									)}
								</Col>
								<Col md={5}>
									{data.selectedInstallByChannel["EMAIL"] && (
										<FormGroup>
											<Label className="fw-semibold mb-1">From Email Address<span className="fw-bold text-danger">{" "}*</span></Label>
											{(() => {
												const selectedInstall = installations.find(
													i => i.id === data.selectedInstallByChannel["EMAIL"]
												);
												const emails = getFromForInstall(selectedInstall);
												const allowedDomains = getAllowedDomainsForInstall(selectedInstall);

												// Prepare creatable options from suggestions
												const emailOptions = emails.map((email: any) => ({ value: email, label: email }));

												return (
													<>
														<CreatableSelect
															isClearable
															inputId="from-email-select"
															value={
																fromByChannel["EMAIL"]
																	? { value: fromByChannel["EMAIL"], label: fromByChannel["EMAIL"] }
																	: null
															}
															options={emailOptions}
															onChange={(opt: any) => {
																const val = opt?.value || "";
																const error = validateFrom(val, selectedInstall);
																setFromError(error);
																if (!error) selectFrom("EMAIL", val);
																else selectFrom("EMAIL", ""); // clear in state if invalid
															}}
															onBlur={e => {
																// If user typed but didn't pick, validate the raw input (if needed)
																// If you want to enforce selection, can do it here
															}}
															placeholder={emails.length ? "Type or select a from address..." : "Type your from address..."}
															formatCreateLabel={inputValue => `Use "${inputValue}"`}
															isValidNewOption={inputValue => {
																// Allow typing any valid email, but will validate on select
																return validator.isEmail(inputValue);
															}}
														/>
														{fromError && (
															<div className="text-danger mt-1" style={{ fontSize: "0.95em" }}>{fromError}</div>
														)}
														<FormText color="muted">
															Only addresses matching your verified sending domain are allowed.
															{allowedDomains.length > 0 && (
																<span> Allowed: <b>{allowedDomains.join(", ")}</b></span>
															)}
														</FormText>
													</>
												);
											})()}
										</FormGroup>
									)}
								</Col>
							</Row>
						)}
						{/* Divider between providers */}
						{idx !== CHANNELS.length - 1 && <hr />}
					</React.Fragment>
				))}
			</form>
		</>
	);
};

export default StepChannels;
